import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchProfile, updateProfile } from '../../store/slices/authSlice';
import { generateTraceId, logUserAction } from '../../utils/trace';

function ProfileCard() {
  const dispatch = useDispatch();
  const { profile, profileLoading, profileError } = useSelector((state) => state.auth);

  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    display_name: '',
    phone: '',
    title: '',
    department: '',
  });

  useEffect(() => {
    dispatch(fetchProfile());
  }, [dispatch]);

  useEffect(() => {
    if (profile) {
      setFormData({
        display_name: profile.display_name || '',
        phone: profile.phone || '',
        title: profile.title || '',
        department: profile.department || '',
      });
    }
  }, [profile]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    const traceId = generateTraceId();
    logUserAction(traceId, 'update_profile', { fields: Object.keys(formData) });

    // Only send non-empty values
    const profileData = {};
    if (formData.display_name) profileData.display_name = formData.display_name;
    if (formData.phone) profileData.phone = formData.phone;
    if (formData.title) profileData.title = formData.title;
    if (formData.department) profileData.department = formData.department;

    await dispatch(updateProfile({ profileData, traceId }));
    setIsEditing(false);
  };

  const handleCancel = () => {
    if (profile) {
      setFormData({
        display_name: profile.display_name || '',
        phone: profile.phone || '',
        title: profile.title || '',
        department: profile.department || '',
      });
    }
    setIsEditing(false);
  };

  if (profileLoading && !profile) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded w-full"></div>
            <div className="h-4 bg-gray-200 rounded w-2/3"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Your Profile</h3>
        {!isEditing ? (
          <button
            onClick={() => setIsEditing(true)}
            className="text-sm text-primary-600 hover:text-primary-700 font-medium"
          >
            Edit
          </button>
        ) : (
          <div className="flex gap-2">
            <button
              onClick={handleCancel}
              className="text-sm text-gray-600 hover:text-gray-700 font-medium"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={profileLoading}
              className="text-sm text-primary-600 hover:text-primary-700 font-medium disabled:opacity-50"
            >
              {profileLoading ? 'Saving...' : 'Save'}
            </button>
          </div>
        )}
      </div>

      {profileError && (
        <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
          {profileError}
        </div>
      )}

      <div className="space-y-4">
        {/* Email - always read-only */}
        <div>
          <label className="block text-sm font-medium text-gray-500 mb-1">Email</label>
          <div className="text-gray-900 bg-gray-50 px-3 py-2 rounded-lg border border-gray-200">
            {profile?.email}
            <span className="ml-2 text-xs text-gray-400">(cannot be changed)</span>
          </div>
        </div>

        {/* Display Name */}
        <div>
          <label className="block text-sm font-medium text-gray-500 mb-1">Display Name</label>
          {isEditing ? (
            <input
              type="text"
              name="display_name"
              value={formData.display_name}
              onChange={handleChange}
              placeholder="Your display name"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          ) : (
            <div className="text-gray-900">
              {profile?.display_name || <span className="text-gray-400 italic">Not set</span>}
            </div>
          )}
        </div>

        {/* Title */}
        <div>
          <label className="block text-sm font-medium text-gray-500 mb-1">Title</label>
          {isEditing ? (
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="Your job title"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          ) : (
            <div className="text-gray-900">
              {profile?.title || <span className="text-gray-400 italic">Not set</span>}
            </div>
          )}
        </div>

        {/* Department */}
        <div>
          <label className="block text-sm font-medium text-gray-500 mb-1">Department</label>
          {isEditing ? (
            <input
              type="text"
              name="department"
              value={formData.department}
              onChange={handleChange}
              placeholder="Your department"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          ) : (
            <div className="text-gray-900">
              {profile?.department || <span className="text-gray-400 italic">Not set</span>}
            </div>
          )}
        </div>

        {/* Phone */}
        <div>
          <label className="block text-sm font-medium text-gray-500 mb-1">Phone</label>
          {isEditing ? (
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="Your phone number"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          ) : (
            <div className="text-gray-900">
              {profile?.phone || <span className="text-gray-400 italic">Not set</span>}
            </div>
          )}
        </div>

        {/* Auth Providers */}
        {profile?.auth_providers && profile.auth_providers.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-gray-500 mb-1">Connected Accounts</label>
            <div className="flex gap-2">
              {profile.auth_providers.map((provider) => (
                <span
                  key={provider}
                  className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800"
                >
                  {provider === 'google' && 'Google'}
                  {provider === 'salesforce' && 'Salesforce'}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ProfileCard;
